import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ImportPanel } from "./import/ImportPanel";
import { api } from "@/api/client";
import { toast } from "sonner";

export function NewProjectDialog({ open, onOpenChange, onCreated, onQuotaExceeded }) {
  const [name, setName] = useState("");
  const [projectId, setProjectId] = useState(null);
  const [defaultTab, setDefaultTab] = useState("archimate");
  const [mode, setMode] = useState(null);
  const navigate = useNavigate();

  const reset = () => {
    setName("");
    setProjectId(null);
    setMode(null);
  };

  const handleQuotaError = (err) => {
    if (err.response?.status === 403 && err.response?.data?.detail?.code === "quota_exceeded") {
      onQuotaExceeded?.(err.response.data.detail.message);
      return true;
    }
    return false;
  };

  const createBlank = async () => {
    if (!name.trim()) {
      toast.error("Give your project a name first");
      return;
    }
    try {
      const res = await api.post("/projects", { name: name.trim() });
      onOpenChange(false);
      reset();
      navigate(`/project/${res.data.id}`);
    } catch (err) {
      if (!handleQuotaError(err)) toast.error("Could not create project");
    }
  };

  const startImport = async (tab) => {
    if (!name.trim()) {
      toast.error("Give your project a name first");
      return;
    }
    try {
      const res = await api.post("/projects", { name: name.trim() });
      setProjectId(res.data.id);
      setDefaultTab(tab);
      setMode("import");
    } catch (err) {
      if (!handleQuotaError(err)) toast.error("Could not create project");
    }
  };

  const handleImportComplete = (viewId) => {
    onOpenChange(false);
    onCreated?.();
    navigate(`/project/${projectId}`, { state: { initialViewId: viewId } });
    reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent className="max-w-2xl" data-testid="new-project-dialog">
        <DialogHeader>
          <DialogTitle>New EA Project</DialogTitle>
          <DialogDescription>Start a blank architecture, or import one from a file or document.</DialogDescription>
        </DialogHeader>
        {!mode && (
          <div className="space-y-4">
            <Input
              data-testid="new-project-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Project name, e.g. Core Banking Architecture"
            />
            <div className="grid grid-cols-3 gap-3">
              <Button data-testid="new-project-blank-button" variant="outline" onClick={createBlank}>
                Start Blank
              </Button>
              <Button data-testid="new-project-import-archimate-button" variant="outline" onClick={() => startImport("archimate")}>
                Import .archimate
              </Button>
              <Button data-testid="new-project-import-document-button" variant="outline" onClick={() => startImport("document")}>
                Import via AI
              </Button>
            </div>
          </div>
        )}
        {mode === "import" && projectId && <ImportPanel projectId={projectId} onComplete={handleImportComplete} defaultTab={defaultTab} />}
      </DialogContent>
    </Dialog>
  );
}
